import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { LinkComponent } from '../../atoms/link/link.component';
import TeaserType from '../../../shared/enums/teaser.type.enums';

import { BadgeComponent } from '../../atoms/badge/badge.component';
import { ImageComponent } from '../../atoms/image/image.component';
import ImageType from '../../../shared/enums/image-type.enums';
import BadgeType from '../../../shared/enums/badge-type.enums';

import { IconComponent } from '../../atoms/icon/icon.component';

import { ButtonType } from '../../../shared/enums/button-type.enums';
import LinkType from '../../../shared/enums/link-type.enums';

import { Colors, Headlines, Sizes } from '../../../shared/enums/shared.enums';

import { Router } from '@angular/router';
import { HrefType } from '../../../shared/enums/href-type.enums';
import { Video } from '../../../shared/interfaces/video.interface';
import ViewportType from '../../../shared/enums/viewport-type.enums';
import { Link } from '../../../shared/interfaces/link.interface';
import { ActionType } from '../../../shared/enums/action-type.enums';
import {
  CaasComponents,
  CaasFormField,
  CaasSimpleField,
} from '../../../shared/interfaces/caas/caas.interface';
import { ImagesService } from '../../../services/images.service';
import { CapitalizePipe } from '../../capitalize.pipe';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { TagsService } from '../../../services/tags.service';

@Component({
  selector: 'hdi-teaser',
  standalone: true,
  templateUrl: './teaser.component.html',
  styleUrls: ['./teaser.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    LinkComponent,
    BadgeComponent,
    ImageComponent,
    IconComponent,
    CapitalizePipe,
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class TeaserComponent implements OnChanges {
  @Input() caasData?: CaasComponents['value'][number];
  @Input() viewport: ViewportType = ViewportType.desktop;
  @Input() disabled: boolean = false;
  @Input() type?: TeaserType = TeaserType.SIMPLE;
  @Input() badges?: { type: BadgeType; label: string }[];
  @Input() badgeSize: Sizes = Sizes.XMEDIUM;
  @Input() headline?: string = '';
  @Input() date?: string;
  @Input() description?: string;
  @Input() CTA: ActionType = ActionType.LINK;
  TeaserType = TeaserType;
  @Input() badgeSubline?: string;
  @Input() mediaType: 'image' | 'icon' = 'image';
  /* types props */
  @Input() buttonType?: ButtonType = ButtonType.SECONDARY;
  @Input() linkType?: LinkType = LinkType.PRIMARY_ARROW;
  @Input() iconButton: string = 'arrow-right';
  @Input() href?: string;
  @Input() fragment: string = '';
  @Input() hrefType: HrefType = HrefType.ANGULAR_ROUTE;
  @Input() target?: string;
  @Input() title: string = '';
  @Input() label?: string;
  @Input() labelArray?: string[];
  @Input() headlineSize?: Headlines = Headlines.H4;
  @Input() descriptionSize?: Sizes = Sizes.SMALL;
  @Input() dropdownItems?: Link[];
  Headlines = Headlines;
  ImageType: typeof ImageType = ImageType;
  /* image props */
  @Input() imageSrc?: string;
  @Input() imageAlt?: string;
  @Input() imageTitle?: string;
  @Input() imageBorder = false;
  /* icon props */
  @Input() iconType?: string;
  @Input() iconSize?: Sizes = Sizes.LARGE;
  @Input() iconColor?: Colors = Colors.BLACK;
  /* styles props */
  @Input() border = false;
  @Input() reverse = false;
  @Input() background = Colors.NEUTRAL_LIGHTEST;
  @Input() isHidden = false;
  BgColors = Colors;
  Sizes = Sizes;
  ActionType = ActionType;
  @Input() isPadding?: boolean = true;
  @Input() video: Video | undefined;
  @Input() imageHeight?: string;
  @Input() columns?: number;
  @Input() layout?: string;

  @ViewChild('hdiLink') hdiLink: LinkComponent | undefined;
  @ViewChild('teaser') teaser: ElementRef | undefined;

  @Output() triggerLinkClick = new EventEmitter();

  constructor(
    private router: Router,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private caasBodytextService: CaasBodytextService,
    private cdr: ChangeDetectorRef,
    private imagesService: ImagesService,
    private tagsService: TagsService
  ) {}

  ngOnChanges() {
    if (this.type === TeaserType.STAGE) {
      this.mediaType = 'icon';
    }

    if (this.caasData) {
      if (this.caasData.formData) {
        this.setCaasContent(this.caasData);
        // console.log('Teaser > CaaS Data:', this.caasData)
      } else {
        console.warn('Missing formData in caasData:', this.caasData);
      }
    }

    if (this.type === TeaserType.SMALL || this.type === TeaserType.MEDIUM) {
      this.background = this.teaser?.nativeElement
        .closest('section')
        ?.classList.contains('bg-grey')
        ? Colors.WHITE
        : Colors.NEUTRAL_LIGHTEST;
    }

    this.cdr.detectChanges();
  }

  handleTeaserClick() {
    this.cdr.detectChanges();
    if (this.type === TeaserType.HUB) {
      this.router.navigate([this.href]);
    } else if (this.type === TeaserType.LANDSCAPE && this.hdiLink) {
      this.handleLinkType();
    }
  }

  handleButtonClick() {
    if (this.hrefType === HrefType.ANGULAR_ROUTE) {
      this.router.navigate([this.href]);
      this.document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  handleLinkClick() {
    this.triggerLinkClick.emit();
  }

  /**
   * Processes the provided caasData and sets component properties accordingly.
   * @param caasData - The data object containing teaser information.
   */
  setCaasContent(caasData: CaasComponents['value'][number]) {
    if (!caasData?.formData) {
      console.warn('Teaser: Caas data or formData is undefined:', caasData);
      return;
    }

    const { formData } = caasData;

    // Set Headline
    this.headline = formData.st_header?.value ?? '';

    // Set Description
    this.description = formData.st_short?.value ?? '';

    // Set Image Source and Title
    this.imageSrc = this.setCaasImageSrc(caasData);
    this.imageTitle = formData.st_image_title?.value ?? '';

    // Set Image Alt Text
    if (formData.st_image?.value?.identifier) {
      this.imageAlt =
        this.imagesService.getAltTextById(formData.st_image.value.identifier) ??
        '';
    } else {
      this.imageAlt = '';
    }

    // Set Icon Type
    this.iconType = formData.st_icon?.value?.toString() ?? '';

    // Set Media Type based on Visual Type
    this.mediaType = this.setCaasTeaserMediaType(caasData);

    // Set Badges
    this.badges = this.getBadges(formData.tt_tag?.value?.target?.identifier);

    // Set Link Properties
    if (formData.st_link?.value?.formData) {
      const linkFormData = formData.st_link.value.formData;

      // Set Link Label and Title
      this.label = linkFormData.lt_link_label?.value ?? '';
      this.title = linkFormData.lt_link_title?.value ?? '';

      // Set Href and Fragment
      this.href = this.caasBodytextService.getHref(formData.st_link);
      this.fragment = this.caasBodytextService.getFragment(formData.st_link);

      // Set Href Type
      this.hrefType = this.caasBodytextService.getHrefType(formData.st_link);

      // Set Link Type
      this.linkType = this.caasBodytextService.getButtonLayout(
        formData.st_link
      ) as LinkType;

      // Set Video if present
      const { lt_link_video_type, lt_link_vimeo_id, lt_link_youtube_id } =
        linkFormData;

      this.video = this.caasBodytextService.getVideo(
        lt_link_video_type as CaasFormField,
        lt_link_vimeo_id as CaasSimpleField,
        lt_link_youtube_id as CaasSimpleField
      );
    }

    // Set Teaser Type, Headline Size, Icon Size, and Icon Color
    const { type, headlineSize, iconSize, iconColor } =
      this.setCaasTeaserType(caasData);
    this.type = type;
    this.headlineSize = headlineSize;
    this.iconSize = iconSize;
    this.iconColor = iconColor;

    // Additional Properties (if any)
    // Example: Date, Dropdown Items, etc.
    // You can add more mappings here based on your formData structure
  }

  /**
   * Determines the teaser type based on layout and visual type.
   * @param caasData - The data object containing teaser information.
   * @returns An object containing type, headlineSize, iconSize, and iconColor.
   */
  private setCaasTeaserType(caasData: CaasComponents['value'][number]): {
    type: TeaserType;
    headlineSize: Headlines;
    iconSize: Sizes;
    iconColor: Colors;
  } {
    const layout = this.layout; // Use the passed layout
    const visualType = caasData.formData.st_visual_type?.value.identifier;

    if (layout === 'horizontal') {
      return visualType === 'icon'
        ? {
            type: TeaserType.STAGE,
            headlineSize: Headlines.H3,
            iconSize: Sizes.XXLARGE,
            iconColor: Colors.GREEN,
          }
        : {
            type: TeaserType.LANDSCAPE,
            headlineSize: Headlines.H4,
            iconSize: Sizes.XXLARGE,
            iconColor: Colors.GREEN,
          };
    } else {
      // Vertical Layout with Columns or Image
      if (this.columns) {
        const columns: number = this.columns;

        switch (columns) {
          case 3:
            return {
              type: TeaserType.SMALL,
              headlineSize: Headlines.H4,
              iconSize: Sizes.XXLARGE,
              iconColor: Colors.GREEN,
            };
          case 2:
            return {
              type: TeaserType.MEDIUM,
              headlineSize: Headlines.H4,
              iconSize: Sizes.XXLARGE,
              iconColor: Colors.GREEN,
            };
          case 1:
            return {
              type: TeaserType.LARGE,
              headlineSize: Headlines.H4,
              iconSize: Sizes.XXLARGE,
              iconColor: Colors.GREEN,
            };
          default:
            // Fallback to MEDIUM
            return {
              type: TeaserType.MEDIUM,
              headlineSize: Headlines.H4,
              iconSize: Sizes.XXLARGE,
              iconColor: Colors.GREEN,
            };
        }
      } else {
        // Default to MEDIUM if columns not specified
        return visualType === 'icon'
          ? {
              type: TeaserType.SMALL,
              headlineSize: Headlines.H4,
              iconSize: Sizes.XXLARGE,
              iconColor: Colors.GREEN,
            }
          : {
              type: TeaserType.MEDIUM,
              headlineSize: Headlines.H4,
              iconSize: Sizes.XXLARGE,
              iconColor: Colors.GREEN,
            };
      }
    }
  }

  /**
   * Determines if the teaser should be reversed based on image orientation.
   * @param caasData - The data object containing teaser information.
   * @returns A boolean indicating if the teaser is reversed.
   */
  private setCaasTeaserReverse(
    caasData: CaasComponents['value'][number]
  ): boolean {
    return caasData.formData.st_image_orientation?.value.label === 'Rechts';
  }

  /**
   * Determines the media type based on visual type.
   * @param caasData - The data object containing teaser information.
   * @returns A string indicating the media type ('image' or 'icon').
   */
  private setCaasTeaserMediaType(
    caasData: CaasComponents['value'][number]
  ): 'image' | 'icon' {
    return caasData.formData.st_visual_type?.value.label === 'Bild'
      ? 'image'
      : 'icon';
  }

  /**
   * Extracts the image source URL from caasData.
   * @param caasData - The data object containing teaser information.
   * @returns A string representing the image source URL.
   */
  private setCaasImageSrc(caasData: CaasComponents['value'][number]): string {
    const imageUrlArray = caasData.formData.st_image?.value?.url;

    if (!imageUrlArray) {
      console.warn('TeaserComponent: st_image.url is undefined or empty.');
      return '';
    }

    try {
      const images: {
        src: string;
        media: string;
      }[] = JSON.parse(imageUrlArray);

      if (!Array.isArray(images)) {
        console.warn(
          'TeaserComponent: Parsed st_image.url is not an array:',
          images
        );
        return '';
      }

      // Find the first image that matches the current viewport media query
      const matchedImage = images.find(
        (img) => window.matchMedia(img.media).matches
      );

      if (matchedImage) {
        return matchedImage.src;
      }

      // Fallback to the first image in the array
      return images[0]?.src || '';
    } catch (error) {
      console.error(
        'TeaserComponent: Failed to parse st_image.url JSON:',
        error
      );
      return '';
    }
  }

  /**
   * Handles different link types based on hrefType.
   */
  private handleLinkType() {
    if (this.hdiLink) {
      switch (this.hdiLink.hrefType) {
        case HrefType.OPEN_MODAL_VIDEO:
          this.renderer.setProperty(this.hdiLink, 'showModal', true);
          break;
        default:
          break;
      }
    }
  }

  /**
   * Retrieves badges based on the provided identifier.
   * @param identifier - The identifier for the tag.
   * @returns An array of badge objects or undefined.
   */
  private getBadges(
    identifier: string | undefined
  ): { type: BadgeType; label: string }[] | undefined {
    if (!identifier) return undefined;
    const tag = this.tagsService.getTagByIdentifier(identifier);
    if (!tag) return undefined;
    return [{ type: BadgeType.DEFAULT, label: tag?.name ?? '' }];
  }
}
